import React from "react";
import {
  Button,
  IconButton,
  MenuItem,
  Typography,
  Tooltip,
  Box,
  List,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Form,
  MenuButton,
  TextControl,
  TruncateTooltip,
} from "../../../components";
import { CloseBig, DownloadOutline, Edit, ErrorOutline } from "@tiny/icons";
import useWorkflowsHeader from "./WorkflowsHeader.hooks";
import QuitWorkflows from "./QuitWorkflows";
import WorkflowRestrictions from "../../../customComponents/WorkflowRestrictions";
import FeedbackButton from "../../../customComponents/FeedbackButton";
import FeatureNotAvailableDialog from "../../../customComponents/FeatureNotAvailableDialog";

// #1956: an ACTIVE workflow's canvas is a draft until published, so the customer needs to know that what they see is
// not yet what their contacts follow. Says "saved" rather than "unsaved" deliberately — the draft IS saved, and that is
// exactly the distinction people get wrong. The timer caveat is the known v1 limitation, better stated than discovered.
const PUBLISH_INFO =
  "Changes to the flow itself — steps, delays, branches, the trigger — are saved but not live: contacts keep following " +
  "the published version until you publish, and anyone waiting on a delay picks up the change when that delay ends. " +
  "Edits to an email's content go live as soon as they save, published or not.";

const PUBLISHED_INFO =
  "Everything on this canvas is live. Edit the workflow and this button turns on so you can publish the changes.";

const WorkflowsHeader = (): JSX.Element => {
  const {
    downloadEnabled,
    isDownloading,
    editTitle,
    form,
    formTitle,
    headerControlAction,
    onClickDownload,
    onClickDownloadSms,
    hasSmsStep,
    quitWorkflows,
    saveTitle,
    setFormTitle,
    titleMaxLength,
    toggleEditTitle,
    workflowTitle,
    restrictions,
    viewMode,
    showFeatureNotAvailable,
    closeFeatureNotAvailableDialog,
    isWelcomeSeries,
    allowReentry,
    onToggleReentry,
    showPublishState,
    hasUnpublishedChanges,
    isPublishImpactLoading,
    isSavingWorkflow,
    showPublishConfirmation,
  } = useWorkflowsHeader();
  return (
    <Box
      sx={{
        height: "100%",
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          height: 72,
          backgroundColor: "white",
          border: `1px solid ${theme.palette.grey[60]}`,
        })}
      >
        <Box
          sx={(theme) => ({
            boxSizing: "border-box",
            flex: "1",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            // theme spacing is 0.25rem per unit, so 40/80 here reserved 160px/320px of padding the title could
            // never reclaim — padding does not shrink in flex, so the name collapsed to its minWidth and rendered
            // as a single ellipsised character. The right-hand cluster already reserves 240px of left padding at
            // lg, which is ample separation on its own.
            paddingRight: 4,
            [theme.breakpoints.up("md")]: {
              paddingRight: 8,
            },
            [theme.breakpoints.up("lg")]: {
              paddingRight: 12,
            },
            gap: 1.5,
          })}
        >
          <Box
            sx={() => ({
              flex: "0 0 54px",
              boxSizing: "border-box",
              padding: "0 12px 0 20px",
            })}
          >
            <IconButton size="small" onClick={quitWorkflows}>
              <CloseBig />
            </IconButton>
          </Box>
          <Box
            sx={{
              // 10px let the name shrink to one character before anything else in the row gave way.
              minWidth: 0,
              // Size to the name (still shrinking when long) so the edit pencil sits beside it, not at the far end.
              flex: "0 1 auto",
              overflow: "hidden",
            }}
          >
            {editTitle ? (
              <Form
                form={form}
                onSubmit={saveTitle}
                style={{
                  width: "100%",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <TextControl
                      name="name"
                      onKeyDown={() => {}}
                      autoFocus
                      label="Edit Title"
                      defaultValue={workflowTitle}
                      maxLength={titleMaxLength}
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={!formTitle}
                    >
                      Save
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button variant="outlined" onClick={toggleEditTitle}>
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            ) : (
              <TruncateTooltip arrow>
                <Typography
                  variant="h4"
                  component="h1"
                  fontWeight="bold"
                  color="grey.100"
                  noWrap
                  m={0}
                >
                  {workflowTitle}
                </Typography>
              </TruncateTooltip>
            )}
          </Box>
          {!editTitle && !viewMode && !isWelcomeSeries && (
            <IconButton size="small" onClick={toggleEditTitle}>
              <Edit />
            </IconButton>
          )}
        </Box>
        <Box
          sx={(theme) => ({
            boxSizing: "border-box",
            flex: "0 0 auto",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            padding: "0 12px",
            [theme.breakpoints.up("md")]: {
              gap: 3,
              padding: "0 20px",
            },
            [theme.breakpoints.up("lg")]: {
              gap: 5,
              padding: "0 20px 0 60px",
            },
          })}
        >
          <FeedbackButton />
          {downloadEnabled && (
            <Tooltip
              title={<Typography sx={{ p: 2 }}>Download Reports</Typography>}
              sx={{ px: 2 }}
            >
              {hasSmsStep ? (
                <MenuButton
                  text="Download Stats"
                  variant="text"
                  disabled={isDownloading}
                >
                  <MenuItem onClick={onClickDownload}>
                    Workflow statistics
                  </MenuItem>
                  <MenuItem onClick={onClickDownloadSms}>
                    SMS statistics
                  </MenuItem>
                </MenuButton>
              ) : (
                <Button
                  variant="text"
                  endIcon={<DownloadOutline />}
                  onClick={onClickDownload}
                  disabled={isDownloading}
                  sx={(theme) => ({
                    [theme.breakpoints.down("md")]: {
                      "& .MuiButton-endIcon": { margin: 0 },
                      minWidth: "auto",
                      px: 1,
                      "& span:first-of-type": { display: "none" },
                    },
                  })}
                >
                  Download Stats
                </Button>
              )}
            </Tooltip>
          )}
          <Tooltip
            title={
              <Typography sx={{ p: 2 }}>
                When enabled, a contact that re-qualifies for the trigger will
                re-enter this workflow from the beginning.
              </Typography>
            }
          >
            <FormControlLabel
              sx={{ m: 0, whiteSpace: "nowrap" }}
              control={
                <Switch
                  checked={allowReentry}
                  onChange={(e) => onToggleReentry(e.target.checked)}
                  disabled={viewMode}
                />
              }
              label="Allow Re-entry"
            />
          </Tooltip>
          {showPublishState && (
            // Deliberately ONE element, always rendered at a fixed label. The right-hand cluster is flex:0 0 auto,
            // so anything added here comes straight out of the workflow title; a button that appears and disappears
            // also shifts every control beside it. Enabled state alone carries whether there is anything to publish.
            <Tooltip
              title={
                <Typography sx={{ p: 2 }}>
                  {hasUnpublishedChanges ? PUBLISH_INFO : PUBLISHED_INFO}
                </Typography>
              }
              componentsProps={{
                popper: {
                  sx: { "& .MuiTooltip-tooltip": { maxWidth: 280 } },
                },
              }}
              arrow
              placement="bottom-end"
            >
              {/* span so the tooltip still fires while the button is disabled */}
              <span data-testid="workflow-publish-state">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    !hasUnpublishedChanges ||
                    isPublishImpactLoading ||
                    isSavingWorkflow
                  }
                  onClick={() => showPublishConfirmation?.()}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Publish
                </Button>
              </span>
            </Tooltip>
          )}
          <WorkflowRestrictions restrictions={restrictions} />
          {headerControlAction.children ? (
            <MenuButton
              disabled={headerControlAction.disabled}
              color="primary"
              text={headerControlAction.text}
              variant="contained"
            >
              {headerControlAction.children
                ?.filter((a) => !a.hidden)
                ?.map((action) => (
                  <MenuItem
                    key={action.text}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.text}
                  </MenuItem>
                ))}
            </MenuButton>
          ) : (
            <Button
              variant="contained"
              disabled={headerControlAction.disabled}
              onClick={headerControlAction.onClick}
            >
              {headerControlAction.text}
            </Button>
          )}
        </Box>
      </Box>
      <QuitWorkflows />
      <FeatureNotAvailableDialog
        open={showFeatureNotAvailable}
        onClose={closeFeatureNotAvailableDialog}
      />
    </Box>
  );
};

export default WorkflowsHeader;
